function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Product Form Web Component: iamVariantPicker <iam-variant-picker>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// The Variant Picker is responsible for handling the variant selection UI.
// - Highlights the selected variant options
// - Enables/disables options based on variant availability
// ----------------------------------------------------------------------------
// Event Listeners:
// - `productForm:option:changed`       → Listens for variant option changes to
//                                        update the variant selector UI
// - `mouseenter` and `mouseleave`      → Listens for hover events to toggle swatch
//                                        option labels
// ----------------------------------------------------------------------------
// Event Filters:
// - `productForm:variant:availability` → Allows other modules to filter the
//                                        variant availability based on custom logic
// ----------------------------------------------------------------------------
// Gotchas:
// - Swatch labels must be properly structured within `.swatch__option` to update correctly.
// - Dynamic availability updates only apply to Options 2+3; Option 1 is initalized on load.
// - Option variants that don't exist in the product data are automatically disabled.
//   (e.g. if Size:Medium does not come in Color:Red)
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import iamFormElement from 'scripts/classes/iamFormElement';

// Styles
import './_product-variant-picker.scss';

// -------------------------------------
// Functionality
// -------------------------------------

class iamProductVariantPicker extends iamFormElement {
  // Class for identifying option containers

  /**
   * Initialize the component when the product form is initialized
   */
  onFormInit() {
    // Subscribe to changes in variant options
    iamotaEventHandler.subscribe('productForm:option:changed', this.onOptionChange.bind(this), {
      context: this.iamForm,
      source: "product-variant-picker.js"
    });

    // Add hover event listeners for swatch options to temporarily display the hovered option
    this.querySelectorAll('.swatch__option input[data-single-option-selector] + label.form__option-label').forEach(swatchOption => {
      swatchOption.addEventListener('mouseenter', this.onOptionHover.bind(this));
      swatchOption.addEventListener('mouseleave', this.updateOptionLabels.bind(this)); // Reverts label on hover exit
    });
  }

  /**
   * Callback when a variant option is changed.
   * Updates the availability, labels, and URL accordingly.
   * @param {Object} event - The event object containing the changed option
   */
  onOptionChange() {
    this.updateOptionAvailability();
    this.updateOptionLabels();
  }

  /**
   * Updates the option label based on the user's selection or hover state.
   * @param {Element} option - The DOM element for the option being updated
   * @param {String} label - The new label text to display
   */
  setOptionLabel(option, label) {
    var subtitle = option.closest('.form__element').querySelector('.swatch__title .swatch__subtitle');
    subtitle.textContent = label; // Set the subtitle to the updated label value
  }

  /**
   * Updates the labels for all selected options.
   * Reflects the current selection for each swatch option.
   */
  updateOptionLabels() {
    this.querySelectorAll('.swatch__option input[data-single-option-selector]:checked').forEach(swatchOption => {
      this.setOptionLabel(swatchOption, swatchOption.value); // Set the label to the selected value
    });
  }

  /**
   * Temporarily updates the option label during hover.
   * Displays the option value the user is hovering over.
   * @param {Event} event - The hover event
   */
  onOptionHover(event) {
    var target = event.currentTarget;
    var newLabel = target.textContent;
    this.setOptionLabel(target, newLabel); // Temporarily show hovered label
  }

  /**
   * Updates the availability of options based on the current variant selection.
   * Only options 2 and 3 are updated, as option 1 remains available by default.
   */
  updateOptionAvailability() {
    var _this = this;
    // Get the currently selected options
    var selectedOptions = this.iamForm.getCurrentOptions();

    // Get the option wrappers for options 2 and 3
    var optionWrappers = Array.from(this.querySelectorAll(iamProductVariantPicker.OPTION_WRAPPER_CLASS));
    optionWrappers = optionWrappers.slice(1); // Exclude option 1 from availability updates (DOM pre-loaded it)

    // Update the availability for each option
    optionWrappers.forEach((option, index) => {
      var optionIndex = index + 2; // Offset to skip option 1
      var optionInputs = option.querySelectorAll('input[type="radio"], option'); // Get input fields

      // Filter variants to get those available based on selected options
      var availableOptions = this.iamForm.productData.variants
      // Only consider "available" variants (i.e. otherwise Shopify will not allow you to add to cart)
      .filter(variant => variant.available)

      // Filter variants that match selected options
      .filter(variant => {
        return selectedOptions.slice(0, optionIndex - 1) // Extract the relevent selected options
        .every((opt, idx) => variant["option".concat(idx + 1)] === opt.value); // Ensure variant matches selected options
      })

      // Allow external filtering of available variants (i.e. custom logic)
      .filter(/*#__PURE__*/function () {
        var _ref = _asyncToGenerator(function* (variant) {
          return yield iamotaEventHandler.applyFilters('productForm:variant:availability', {
            variant
          }, {
            context: _this.iamForm
          });
        });
        return function (_x) {
          return _ref.apply(this, arguments);
        };
      }())

      // Get the available values for the current option
      .map(variant => variant["option".concat(optionIndex)]);

      // Update availability for the option
      this.setOptionAvailability(optionInputs, availableOptions);
    });
  }

  /**
   * Sets the availability status for the variant options.
   * Toggles the disabled state based on availability.
   * @param {NodeList} listOfOptions - The list of option inputs
   * @param {Array} availableOptions - The options that are currently available
   */
  setOptionAvailability(listOfOptions, availableOptions) {
    listOfOptions.forEach(input => {
      var swatchOption = input.closest('.swatch__option'); // Find the swatch option wrapper
      if (swatchOption) {
        var label = swatchOption.querySelector('.form__option-label');
        var isSwatchDisabled = !availableOptions.includes(input.getAttribute('value')); // Determine if the option is unavailable
        label.classList.toggle('swatch--disabled', isSwatchDisabled); // Toggle the disabled state
      }
    });
  }
}

// Register the custom element with the browser
// Variables
_defineProperty(iamProductVariantPicker, "OPTION_WRAPPER_CLASS", '.selector-wrapper');
customElements.define('iam-variant-picker', iamProductVariantPicker);